import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const isSafeNext = (value) => typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

export default function LoginPage() {
  useSEO({
    title: 'Sign In | Acodia — Grow. Defend. Be Found.',
    description: 'Sign in to your Acodia account to access your Client Pulse Dashboard.',
    path: '/login',
    robots: 'noindex,nofollow',
  });

  const { login, user, tokens } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get('next');
  const redirectTarget = isSafeNext(nextParam) ? nextParam : '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tokens && user) {
      navigate(redirectTarget, { replace: true });
    }
  }, [tokens, user, redirectTarget, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, requiresTotp ? totpCode : null);
      if (result.requires_totp) {
        setRequiresTotp(true);
        return;
      }
      window.location.href = redirectTarget;
    } catch (err) {
      setError('Email or password incorrect');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.025)',
    border: '1px solid var(--b1)',
    color: 'var(--white)',
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--bg)' }}
    >
      <div className="w-full max-w-md">
        <a href="/" className="block text-center mb-8">
          <span className="font-display text-2xl font-bold" style={{ color: 'var(--white)' }}>acodia</span>
        </a>

        <div
          className="p-8 rounded-lg"
          style={{
            background: 'rgba(10,10,18,0.72)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(139,92,246,0.18)',
            boxShadow: '0 0 60px rgba(139,92,246,0.07)',
          }}
        >
          <div className="mb-6">
            <h1 className="font-display text-xl font-extrabold mb-1" style={{ color: 'var(--white)' }}>
              Sign in
            </h1>
            <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>
              Welcome back. Access your Client Pulse Dashboard.
            </p>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="flex items-center gap-2 p-3 rounded mb-4"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}
            >
              <AlertCircle size={14} style={{ color: 'var(--danger)', flexShrink: 0 }} />
              <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="font-body text-sm h-11 rounded-[3px] pl-9"
                  style={inputStyle}
                  required
                  autoFocus
                />
              </div>
            </div>

            {!requiresTotp && (
              <div>
                <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                  <Input
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className="font-body text-sm h-11 rounded-[3px] pl-9 pr-10"
                    style={inputStyle}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--te)' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            )}

            {requiresTotp && (
              <div>
                <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--a)' }}>Authenticator Code</label>
                <Input
                  data-testid="login-totp-input"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  className="font-mono text-lg text-center h-12 rounded-[3px] tracking-[0.3em]"
                  style={inputStyle}
                  maxLength={6}
                  autoFocus
                />
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full btn-shimmer font-body font-medium text-sm h-11 rounded-[3px]"
              style={{ background: 'var(--a)', color: '#fff', opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Signing in…' : requiresTotp ? 'Verify Code' : 'Sign In'}
              <ArrowRight size={14} className="ml-2" />
            </Button>
          </form>

          <div
            className="mt-6 pt-5 text-center"
            style={{ borderTop: '1px solid var(--b1)' }}
          >
            <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>
              New to Acodia Watch?{' '}
              <a
                href="/watch"
                data-testid="login-start-trial-link"
                className="font-medium underline underline-offset-2"
                style={{ color: 'var(--a)' }}
              >
                Start your free trial
              </a>
            </p>
          </div>
        </div>

        <p className="text-center mt-4 font-body text-xs" style={{ color: 'var(--te)' }}>
          By signing in you agree to our{' '}
          <a href="/terms" className="underline">Terms of Service</a> and{' '}
          <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
